// Product-line ordering + hard-restriction for the Edit-Estimate SKU picker.
//
// A job's productLine ('gutter' | 'insulation') decides which categories the picker
// offers: the job's own trade priority group FIRST (fixed order), then everything that
// belongs to NEITHER trade ("shared" accessories) alphabetically, with the OTHER trade's
// priority categories EXCLUDED entirely (hard restrict — no show-all toggle).
//
// DELIBERATELY name-keyed and SEPARATE from product-line.ts: that module's
// INSULATION_CATEGORY_NAMES lumps 'Safety/PPE' with insulation (for pricing/attr logic),
// but the PICKER treats Safety/PPE as SHARED (shown on both trades). Do NOT "reconcile"
// the two — they answer different questions. (Until categories carry a real product_line
// column, both are name-based; this one owns picker grouping only.)

import type { ProductLine, SkuCategory } from '@gutterinv/shared';

/** Per-trade PRIORITY categories, in the exact display order the office wants pinned. */
export const PICKER_PRIORITY_BY_LINE: Record<ProductLine, string[]> = {
  gutter: ['Gutter Coil', 'Downspouts', 'Gutter Guards', 'Elbows', 'End Caps', 'Miters'],
  insulation: ['Insulation', 'Insulation Accessories'],
};

export interface OrderedPickerCategories {
  /** This trade's priority categories, in PICKER_PRIORITY_BY_LINE order. */
  priority: SkuCategory[];
  /** Categories belonging to neither trade's priority list, alphabetical. */
  shared: SkuCategory[];
  /** The OTHER trade's priority names — excluded from the picker + the SKU list. */
  excludedNames: Set<string>;
}

/**
 * Order + restrict the picker categories for a job's product line.
 * Returns `null` when `line` is null/unknown → the caller shows ALL categories
 * unfiltered (never an empty picker).
 *
 * An unknown/new category name (in neither priority list) lands in `shared` → shown,
 * never silently hidden. NOTE the leak direction: exclusion is by the other trade's
 * priority NAMES, so a renamed priority entry stops being excluded and would surface in
 * the other trade — hence the dev-time warn below when a configured name is absent.
 */
export function orderPickerCategories(
  categories: SkuCategory[],
  line: ProductLine | null,
): OrderedPickerCategories | null {
  if (!line) return null;
  const ownPriority = PICKER_PRIORITY_BY_LINE[line];
  const otherLine: ProductLine = line === 'gutter' ? 'insulation' : 'gutter';
  const excludedNames = new Set(PICKER_PRIORITY_BY_LINE[otherLine]);
  const byName = new Map(categories.map((c) => [c.name, c]));

  // Leak guard: a configured priority name missing from live categories means a rename —
  // it would silently leak the other trade in. Warn loudly instead of failing silent.
  for (const name of ownPriority) {
    if (!byName.has(name)) {
      console.warn(
        `[sku-picker-order] priority category "${name}" not found in categories — ` +
          `picker order/exclusion may be wrong (renamed?).`,
      );
    }
  }

  const ownPrioritySet = new Set(ownPriority);
  const priority = ownPriority
    .map((name) => byName.get(name))
    .filter((c): c is SkuCategory => c != null);
  const shared = categories
    .filter((c) => !ownPrioritySet.has(c.name) && !excludedNames.has(c.name))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { priority, shared, excludedNames };
}
